import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Image,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { toggleBGM } from "../../redux/action/action";
import { COMMON_WEB_PATH } from "../../constant";
import { useTranslation } from "react-i18next";
import useIsLandScapeMachine from "../../hook/useIsLandScapeMachine";

const VolumeSlider = ({ isOpen, onClose }) => {
  const isBGMEnabled = useSelector((state) => state.isBGMEnabled);
  const { enabled, musicVolume = 0.2 } = isBGMEnabled || {};
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isMachineLandscape = useIsLandScapeMachine();

  const sliderRef = useRef(null);
  // 使用 Redux 音量值作為初始狀態
  const [volume, setVolume] = useState(musicVolume * 100);
  // 儲存上一次非零的音量值 如果音量為0，則預設為20
  const [lastNonZeroVolume, setLastNonZeroVolume] = useState(
    musicVolume > 0 ? musicVolume * 100 : 20
  );
  // 追蹤是否正在拖動滑塊
  const [isDragging, setIsDragging] = useState(false);
  // 控制滑塊顯示的音量值
  const [displayVolume, setDisplayVolume] = useState(
    enabled ? musicVolume * 100 : 0
  );

  // 根據音量和啟用狀態決定顯示哪個圖標
  const shouldShowMuteIcon = volume === 0 || !enabled;

  // 更新組件內部音量狀態，但不立即分發到 Redux
  const handleVolumeChange = (val) => {
    setDisplayVolume(val);
    setVolume(val);

    // 保存非零音量值
    if (val > 0) {
      setLastNonZeroVolume(val);
    }

    // 當音量設為 0 時，自動將音樂設為靜音
    if (val === 0 && enabled) {
      dispatch(
        toggleBGM({
          ...isBGMEnabled,
          enabled: false,
          musicVolume: 0,
        })
      );
    }
    // 當音量從 0 調高，且當前是靜音，自動取消靜音
    else if (val > 0 && !enabled) {
      dispatch(
        toggleBGM({
          ...isBGMEnabled,
          enabled: true,
          musicVolume: val / 100,
        })
      );
    }
  };

  // 當滑塊釋放時，才向 Redux更新的音量值
  const handleVolumeChangeEnd = (val) => {
    const newVolume = val / 100;

    // 保存非零音量值
    if (val > 0) {
      setLastNonZeroVolume(val);
    }

    // 更新 Redux 狀態
    dispatch(
      toggleBGM({
        ...isBGMEnabled,
        musicVolume: newVolume,
        // 確保音量為 0 時自動靜音
        ...(val === 0 ? { enabled: false } : {}),
        // 確保音量大於 0 時取消靜音
        ...(val > 0 ? { enabled: true } : {}),
      })
    );

    // 結束拖動狀態
    setIsDragging(false);
  };

  // 開始拖動滑塊
  const handleVolumeChangeStart = () => {
    setIsDragging(true);
  };

  // 靜音切換
  const toggleMute = (e) => {
    e.stopPropagation(); // 防止事件冒泡

    // 只有沒有拖動滑塊時才切換靜音狀態
    if (!isDragging) {
      if (enabled) {
        // 靜音：保存當前音量，設置靜音狀態，並將顯示滑塊設為0
        dispatch(
          toggleBGM({
            ...isBGMEnabled,
            enabled: false,
            // 保持音量值不變，以便稍後恢復
          })
        );
        // 更新顯示的滑塊位置為0
        setDisplayVolume(0);
      } else {
        // 取消靜音：恢復到上次的非零音量
        const volumeToRestore = lastNonZeroVolume > 0 ? lastNonZeroVolume : 20;
        dispatch(
          toggleBGM({
            ...isBGMEnabled,
            enabled: true,
            musicVolume: volumeToRestore / 100,
          })
        );
        // 更新顯示的滑塊位置
        setDisplayVolume(volumeToRestore);
        // 同時更新實際音量值
        setVolume(volumeToRestore);
      }
    }
  };

  // 當 Redux 中的音量或啟用狀態發生變化時，同步更新組件狀態
  useEffect(() => {
    // 更新實際音量值
    setVolume(musicVolume * 100);

    // 保存非零音量
    if (musicVolume > 0) {
      setLastNonZeroVolume(musicVolume * 100);
    }

    // 根據啟用狀態更新顯示音量
    setDisplayVolume(enabled ? musicVolume * 100 : 0);
  }, [musicVolume, enabled]);

  // 監聽點擊外部關閉滑塊
  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (event) => {
        if (
          sliderRef.current &&
          !sliderRef.current.contains(event.target) &&
          !event.target.closest(".music-toggle-button")
        ) {
          // 只有沒有拖動滑塊時才關閉滑塊
          if (!isDragging) {
            onClose();
          }
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, onClose, isDragging]);

  if (!isOpen) return null;

  return (
    <Box
      ref={sliderRef}
      position="absolute"
      top={isMachineLandscape ? "100%" : "65px"}
      zIndex={2000}
      onClick={(e) => e.stopPropagation()} // 防止點擊滑塊時關閉它
    >
      <Box
        minWidth="30px"
        minHeight="200px"
        pt="35px"
        pb="10px"
        px="15px"
        position="relative"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-start"
        padding="10px"
        borderRadius="full"
        border={`1px solid #5c4b6a`}
        background="rgba(33, 14, 60, 0.9)"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
      >
        <Flex flexDirection="column" alignItems="center" height="100%">
          <Box mb={3} cursor="pointer" onClick={toggleMute}>
            <Image
              src={
                shouldShowMuteIcon
                  ? `${COMMON_WEB_PATH}/911TW/mute.webp`
                  : `${COMMON_WEB_PATH}/911TW/music.webp`
              }
              width="30px"
              height="30px"
              alt={shouldShowMuteIcon ? "取消靜音" : "靜音"}
            />
          </Box>
          <Box
            height="130px"
            width="100%"
            display="flex"
            justifyContent="center"
          >
            <Slider
              aria-label="音量調整"
              value={displayVolume}
              onChange={handleVolumeChange}
              onChangeStart={handleVolumeChangeStart}
              onChangeEnd={handleVolumeChangeEnd}
              min={0}
              max={100}
              colorScheme="purple"
              orientation="vertical" // 垂直
              minH="120px"
            >
              <SliderTrack
                bg="#000"
                width="10px"
                borderRadius="full"
                border={`2px solid #8946a9`}
              >
                <SliderFilledTrack
                  background={`linear-gradient(to bottom, #6233A5, #6233A5)`}
                  bgPosition="center"
                  bgRepeat="no-repeat"
                />
              </SliderTrack>
              <SliderThumb
                boxSize={5}
                bgImage={`${COMMON_WEB_PATH}/911TW/volumeThumb.webp`}
                bgSize="100% 100%"
                border="2px solid #8500ff"
                _focus={{ boxShadow: "0 0 0 3px rgba(133, 0, 255, 0.3)" }}
              />
            </Slider>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default VolumeSlider;
